import { useState, useCallback } from 'react'
import type * as jsforce from 'jsforce'
import type { PRSheetData } from '../types/salesforce'
import { generatePRSheetHTML, generatePDF } from '../services/pdfGenerator'
import { savePDFToSalesforceFiles, savePRSheet } from '../services/salesforce'

/**
 * PDF生成・プレビュー管理フック
 *
 * 責務:
 * - PDF生成処理（HTML生成 → PDF変換 → Salesforce Files保存）
 * - プレビューダイアログの開閉状態管理
 * - PDF作成完了ダイアログの状態管理
 */
export function usePDFGenerator(
  data: PRSheetData | null,
  onDataUpdate: (data: PRSheetData) => void,
  onError: (message: string, isSessionExpired: boolean) => void,
) {
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [previewOpen, setPreviewOpen] = useState(false)
  const [pdfSuccessDialogOpen, setPdfSuccessDialogOpen] = useState(false)
  const [pdfContentDocumentId, setPdfContentDocumentId] = useState<string | null>(null)

  const handleGeneratePDF = useCallback(
    async (conn: jsforce.Connection) => {
      if (!data) return

      setGeneratingPDF(true)
      try {
        // HTML生成（非同期）
        const html = await generatePRSheetHTML(data)

        // Lambda関数でPDF生成（PDFバイナリを取得）
        const pdfBlob = await generatePDF(html)

        // Salesforce Filesに保存
        if (data.prSheet.Id) {
          const fileName = `PRSheet_${data.career.Name}_${new Date().toISOString().split('T')[0]}.pdf`

          // 既存のPDFContentDocumentIdを渡す（バージョン管理）
          const contentDocumentId = await savePDFToSalesforceFiles(
            conn,
            data.prSheet.Id,
            pdfBlob,
            fileName,
            data.prSheet.PDFContentDocumentId__c,
          )

          // PRSheetレコードのPDFContentDocumentId__cを更新
          await savePRSheet(conn, {
            ...data.prSheet,
            PDFContentDocumentId__c: contentDocumentId,
          })

          // ローカルデータも更新
          const updatedData = {
            ...data,
            prSheet: {
              ...data.prSheet,
              PDFContentDocumentId__c: contentDocumentId,
            },
          }
          onDataUpdate(updatedData)

          // ContentDocumentIdを保存してダイアログを表示
          setPdfContentDocumentId(contentDocumentId)
          setPdfSuccessDialogOpen(true)
        }
      } catch (error) {
        console.error('PDF生成エラー:', error)

        // セッションエラーの場合はログイン画面にリダイレクト
        const errorMessage = error instanceof Error ? error.message : String(error)
        const isSessionExpired = errorMessage.includes('INVALID_SESSION_ID') || errorMessage.includes('Session expired')

        onError('PDF生成に失敗しました', isSessionExpired)
      } finally {
        setGeneratingPDF(false)
      }
    },
    [data, onDataUpdate, onError],
  )

  return {
    generatingPDF,
    previewOpen,
    setPreviewOpen,
    pdfSuccessDialogOpen,
    setPdfSuccessDialogOpen,
    pdfContentDocumentId,
    handleGeneratePDF,
  }
}
